
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { AddGymDialog } from '@/components/gyms/add-gym-dialog';
import { useNavigate } from 'react-router-dom';
import { BuildingIcon, RefreshCw, AlertCircle } from 'lucide-react';
import { useUser } from '@/contexts/user-context';
import { useGym } from '@/contexts/gym-context';
import { useTenantSubscription } from '@/api/tenants';
import { Alert, AlertDescription } from '@/components/ui/alert';

export function NoGymMessage() {
  const { t } = useTranslation();
  const [showAddGymDialog, setShowAddGymDialog] = useState(false);
  const navigate = useNavigate();
  const { user } = useUser();
  const { fetchGyms } = useGym();

  // Get subscription to check gym limits
  const { data: subscription } = useTenantSubscription();
  const gymLimitReached = subscription
    ? subscription.gymCount >= subscription.gymLimit
    : false;

  const handleRefresh = async () => {
    await fetchGyms();
    window.location.reload();
  };

  return (
    <div className="flex items-center justify-center h-[80vh]">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <BuildingIcon className="h-16 w-16 text-primary" />
          </div>
          <CardTitle>{t('dashboard.noGymTitle')}</CardTitle>
          <CardDescription>{t('dashboard.noGymDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            {t(
              'dashboard.createGymInstructions',
              'To get started, create your first gym by clicking the button below.'
            )}
          </p>

          {gymLimitReached && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t('gyms.gymLimitReachedMessage')}
              </AlertDescription>
            </Alert>
          )}

          {/* Debug information */}
          <div className="mt-4 p-3 bg-muted rounded-md text-xs">
            <p className="font-semibold mb-1">Debug Info:</p>
            <p>User ID: {user?._id || 'Not available'}</p>
            <p>Gym IDs: {user?.gym?.join(', ') || 'None'}</p>
            {subscription && (
              <p>
                Gym Usage: {subscription.gymCount} / {subscription.gymLimit}
              </p>
            )}
          </div>
        </CardContent>
        <CardFooter className="flex flex-col gap-2">
          <Button
            className="w-full"
            onClick={() => setShowAddGymDialog(true)}
            disabled={gymLimitReached}
          >
            {t('dashboard.createGymNow')}
          </Button>
          <Button variant="outline" className="w-full" onClick={handleRefresh}>
            <RefreshCw className="mr-2 h-4 w-4" />
            {t('common.refresh')}
          </Button>
        </CardFooter>
      </Card>

      <AddGymDialog
        open={showAddGymDialog}
        onOpenChange={(open) => {
          setShowAddGymDialog(open);
          if (!open) {
            // Refresh the page to check if gym was created
            window.location.reload();
          }
        }}
      />
    </div>
  );
}
